import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const USER_API_URL = `${BASE_API_URL}/permissionUsers`;

export interface User {
  id: number;
  userName: string;
  email?: string;
  roleId?: number;
  roleName?: string;
  isActive?: boolean;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserResponse {
  message?: string;
  detail?: string;
}

export interface SystemConfItem {
  id: number;
  name: string;
  value: string;
  description?: string;
}

/**
 * Get all system config rows (password policy, etc).
 * Calls GET /api/systemConf
 */
export const getSystemConfigs = async (): Promise<SystemConfItem[]> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<SystemConfItem[]>(`${BASE_API_URL}/systemConf`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to get system configs:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to get system configs'
    );
  }
};

export interface PasswordPolicyConfig {
  minLength: number;
  isStrong: boolean;
}

/**
 * Fetch and parse the Password Length / Password Complexity policy from the
 * systemConf table. Call this once (e.g. on form mount) and reuse the result
 * with checkPasswordAgainstPolicy for live, per-keystroke validation, instead
 * of re-fetching on every change.
 */
export const getPasswordPolicyConfig = async (): Promise<PasswordPolicyConfig> => {
  const configs = await getSystemConfigs();
  const map: { [key: string]: string } = {};
  configs.forEach((c) => { map[c.name] = c.value; });

  const minLength = parseInt(map['Password Length'] ?? '', 10) || 6;
  const complexity = (map['Password Complexity'] ?? 'weak').trim().toLowerCase();
  return { minLength, isStrong: complexity === 'strong' };
};

/**
 * Synchronously check a real (not-yet-hashed) password against an already-fetched
 * policy. Returns an error message stating the full current policy (length AND
 * complexity together, not just whichever rule failed) if violated, otherwise null.
 */
export const checkPasswordAgainstPolicy = (password: string, policy: PasswordPolicyConfig): string | null => {
  const { minLength, isStrong } = policy;

  const lengthOk = !!password && password.length >= minLength;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const complexityOk = !isStrong || (hasUpper && hasLower && hasDigit);

  if (lengthOk && complexityOk) {
    return null;
  }

  let message = `Password must be at least ${minLength} characters long`;
  if (isStrong) {
    message += ' and contain uppercase letters, lowercase letters, and digits';
  }
  message += '.';
  return message;
};

/**
 * Validate a real (not-yet-hashed) password against the Password Length / Password
 * Complexity policy from the systemConf table. Must run on the real password BEFORE
 * it gets SHA-256-hashed for transmission, since the backend only ever sees that hash.
 * Returns an error message if the password violates the policy, otherwise null.
 */
export const validatePasswordAgainstPolicy = async (password: string): Promise<string | null> => {
  const policy = await getPasswordPolicyConfig();
  return checkPasswordAgainstPolicy(password, policy);
};

/**
 * Get all users
 * Calls the GET /api/User/all endpoint
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<User[]>(`${USER_API_URL}/all`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to get all users:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get all users'
    );
  }
};

/**
 * Get user by ID
 * Calls the GET /api/User/{id} endpoint
 */
export const getUserById = async (id: number): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<User>(`${USER_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to get user ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get user'
    );
  }
};

/**
 * Create a new user
 * Calls the POST /api/User endpoint
 */
export const createUser = async (userData: Partial<User>): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post<User>(USER_API_URL, userData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to create user:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create user'
    );
  }
};

/**
 * Update an existing user
 * Calls the PUT /api/User/{id} endpoint
 */
export const updateUser = async (id: number, userData: Partial<User>): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.put<User>(`${USER_API_URL}/${id}`, userData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update user ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to update user'
    );
  }
};

/**
 * Delete a user
 * Calls the DELETE /api/User/{id} endpoint
 */
export const deleteUser = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${USER_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to delete user ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to delete user'
    );
  }
};

/**
 * Soft-delete a user by setting isActive = false, keeping all other fields intact.
 * Calls DELETE /api/permissionUsers/{id}, which the backend implements as a soft
 * deactivate (it only flips isActive and preserves the rest of the record).
 * Note: this intentionally does NOT use PUT /api/permissionUsers/{id}, since that
 * endpoint performs a full-object overwrite and would blank out userName, email,
 * departmentId, roleId, etc. when only { isActive: false } is sent.
 */
export const deactivateUser = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${USER_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to deactivate user ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to deactivate user'
    );
  }
};

/**
 * Unlock a locked user and reset failed login attempts.
 * Calls POST /api/permissionUsers/{id}/unlock
 */
export const unlockUser = async (id: number): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.post(`${USER_API_URL}/${id}/unlock`, null, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to unlock user ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to unlock user'
    );
  }
};

/**
 * Transfer all approval tasks from one user to another.
 * Calls POST /api/UserTaskTransfer
 * Updates QmsStepInstanceApprovers and QmsWorkflowStepApprovers.
 */
export const transferUserTasks = async (
  fromUserId: number,
  toUserId: number,
  comment?: string
): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.post(
      `${BASE_API_URL}/UserTaskTransfer`,
      { fromUserId, toUserId, comment: comment ?? '' },
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error: any) {
    console.error('Failed to transfer user tasks:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to transfer tasks'
    );
  }
};

const sha256Hex = async (value: string): Promise<string> => {
  const enc = new TextEncoder();
  const hashBuf = await crypto.subtle.digest('SHA-256', enc.encode(value));
  return Array.from(new Uint8Array(hashBuf)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Change the current user's own password (self-service).
 * Calls POST /api/auth/change-password with SHA-256-hashed passwords, matching
 * the login/create/edit convention where the backend only ever sees the hash.
 * The caller is responsible for validating the new password against the policy
 * (via validatePasswordAgainstPolicy) BEFORE calling this, since only the real
 * plaintext can be checked for length/complexity.
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    const [currentPasswordHash, newPasswordHash] = await Promise.all([
      sha256Hex(currentPassword),
      sha256Hex(newPassword),
    ]);
    await axios.post(
      `${BASE_API_URL}/auth/change-password`,
      { currentPassword: currentPasswordHash, newPassword: newPasswordHash },
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error: any) {
    console.error('Failed to change password:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to change password'
    );
  }
};
